import { EMAIL_TOPIC_OPTIONS, EMAIL_ENROLLMENT_NOTICE } from "../lib/emailTopics";
import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { value: "general", label: "General Inquiry" },
  { value: "become_national", label: "Become YaSHaRUN" },
  { value: "ministries", label: "Ministries / Volunteering" },
  { value: "support", label: "Support / Donations" },
  { value: "shop", label: "Shop / Orders" },
  { value: "media", label: "Media / Communications" },
  { value: "technology", label: "Technology / Website" },
  { value: "fightfitash", label: "Fight Fi Tash Campaign" },
  { value: "music", label: "Music and releases" },
  { value: "records", label: "Record label and artists" },
  { value: "publishing", label: "Publishing and writers" },
  { value: "lives", label: "Live broadcasts" },
  { value: "teachings", label: "Teachings and studies" },
  { value: "tutorials", label: "Tutorials" },
  { value: "sneak_peeks", label: "Sneak peeks" },
  { value: "exclusives", label: "Exclusive announcements" },
  { value: "weekly_summary", label: "Weekly summary" },
  { value: "monthly_digest", label: "Monthly digest" },
  { value: "programs", label: "Programs" },
  { value: "events", label: "Events and gatherings" },
  { value: "feasts", label: "Feasts and appointed times" },
];

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    reason: "",
    message: "",
    email_topics: [],
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email format";
    if (!formData.reason) newErrors.reason = "Please select a reason";
    if (!formData.message.trim()) newErrors.message = "Message is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Send className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-heading text-3xl text-royal-blue mb-4">Message Sent!</h1>
          <p className="text-slate-600 mb-8">
            Thank you for contacting us. We'll get back to you as soon as possible.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                full_name: "",
                email: "",
                phone: "",
                reason: "",
                message: "",
                email_topics: [],
              });
            }}
            className="btn-gold"
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero */}
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
            Get in Touch
          </span>
          <h1 className="heading-hero mt-2 mb-4">Contact Us</h1>
          <p className="body-large text-slate-300 max-w-2xl">
            Have questions about The Kingdom of Yunah'Shach'Qal Yashar'al? 
            We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="bg-parchment py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-heading text-2xl text-royal-blue mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-kingdom-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-kingdom-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-royal-blue">Email</h3>
                    <a
                      href="mailto:admin@yasharal.vip"
                      className="text-slate-600 hover:text-kingdom-gold"
                    >
                      admin@yasharal.vip
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-kingdom-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-kingdom-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-royal-blue">Phone</h3>
                    <p className="text-slate-600">(224) CALL-YAH (225-5924)</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-kingdom-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-kingdom-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-royal-blue">Address</h3>
                    <p className="text-slate-600">
                      777 Qadash Lane<br />
                      Yunah'Shach'Qal, Yashar'al
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-white rounded-sm shadow">
                <h3 className="font-heading text-lg text-royal-blue mb-2">Direct Email</h3>
                <p className="text-sm text-slate-600 mb-4">
                  For faster response, email us directly:
                </p>
                <a
                  href="mailto:admin@yasharal.vip"
                  className="inline-flex items-center gap-2 text-kingdom-gold hover:underline font-semibold"
                >
                  <Mail className="w-4 h-4" />
                  admin@yasharal.vip
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 rounded-sm shadow-lg">
                <h2 className="font-heading text-2xl text-royal-blue mb-6">Send a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="form-label">Full Name *</Label>
                      <Input
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => handleChange("full_name", e.target.value)}
                        className="form-input"
                        placeholder="Your name"
                        data-testid="contact-name-input"
                        style={{ fontSize: "16px" }}
                      />
                      {errors.full_name && (
                        <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>
                      )}
                    </div>

                    <div>
                      <Label className="form-label">Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        className="form-input"
                        placeholder="your@email.com"
                        data-testid="contact-email-input"
                        style={{ fontSize: "16px" }}
                      />
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="form-label">Phone</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        className="form-input"
                        placeholder="Your phone number"
                        style={{ fontSize: "16px" }}
                      />
                    </div>

                    <div>
                      <Label className="form-label">Reason *</Label>
                      <Select
                        value={formData.reason}
                        onValueChange={(value) => handleChange("reason", value)}
                      >
                        <SelectTrigger className="form-input" data-testid="contact-reason-select">
                          <SelectValue placeholder="Select a reason" />
                        </SelectTrigger>
                        <SelectContent>
                          {REASONS.map((reason) => (
                            <SelectItem key={reason.value} value={reason.value}>
                              {reason.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.reason && (
                        <p className="text-red-500 text-xs mt-1">{errors.reason}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label className="form-label">Message *</Label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      className="form-input min-h-[150px]"
                      placeholder="Your message..."
                      data-testid="contact-message-input"
                      style={{ fontSize: "16px" }}
                    />
                    {errors.message && (
                      <p className="text-red-500 text-xs mt-1">{errors.message}</p>
                    )}
                  </div>

                  <fieldset className="space-y-3">
                    <legend className="font-semibold text-sm">Additional email subjects (optional)</legend>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {EMAIL_TOPIC_OPTIONS.map(([value, label]) => (
                        <label key={value} className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={formData.email_topics.includes(value)}
                            onChange={(e) => handleChange("email_topics", e.target.checked
                              ? [...formData.email_topics, value]
                              : formData.email_topics.filter((item) => item !== value))} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <p className="text-xs text-slate-600">{EMAIL_ENROLLMENT_NOTICE}</p>
                  <Button
                    type="submit"
                    className="w-full md:w-auto btn-gold px-8 py-3"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
